/**
 * SERVER-CORRECTED CLOCK for displays (Oct 2026).
 *
 * WHY: every countdown, tile state and Bid-button visibility was computed from the bidder's OWN
 * computer clock. A machine 30 seconds fast showed every lot closing 30 seconds early and, on the
 * home and sale tiles, removed the Bid button 25 seconds before the lot closed — a complaint we
 * could never verify. This module corrects the display clock by the server's time.
 *
 * HOW: ONE value, fetched ONCE per page load: the HTTP `Date` header of a HEAD request to this
 * site (Firebase Hosting / its CDN, which keep NTP time), adjusted for half the round trip and for
 * any `Age`. offset = serverTime - localTime. serverNow() = Date.now() + offset. No per-tick
 * network call; nothing structural.
 *
 * FALLBACK: if the fetch fails, is slow (> 5s round trip), has no usable Date header, or gives an
 * absurd answer (> 12h), the offset stays 0 — exactly today's behaviour, never a blank clock.
 * The Date header has 1-second resolution, so an offset under 1.5s is treated as "in sync" (0):
 * correcting a good clock by header noise would make it worse, not better.
 *
 * DISPLAY ONLY. No code refuses or allows a bid on this value — the server decides every bid.
 * Diagnostics: window.__wlbClock = { offsetMs, rttMs, syncedAt, status }.
 */

type ClockStatus = 'local' | 'synced' | 'in-sync' | 'failed';

let offsetMs = 0;
let started = false;

const report = (status: ClockStatus, rttMs: number | null, rawOffsetMs: number | null) => {
  try {
    (window as any).__wlbClock = { offsetMs, rawOffsetMs, rttMs, syncedAt: new Date().toISOString(), status };
  } catch { /* no window */ }
};

/** Now, in ms, on the server's clock when known — otherwise the local clock. */
export const serverNow = (): number => Date.now() + offsetMs;

/** The correction currently applied (0 until synced, or if syncing failed). */
export const clockOffsetMs = (): number => offsetMs;

const SYNC_MAX_RTT_MS = 5000;
const SYNC_MIN_CORRECTION_MS = 1500;
const SYNC_MAX_ABS_OFFSET_MS = 12 * 60 * 60 * 1000;

/** Fetch the server's time once. Safe to call repeatedly: only the first call does anything. */
export const syncServerClock = async (): Promise<void> => {
  if (started) return;
  started = true;
  report('local', null, null);
  try {
    if (typeof fetch !== 'function' || typeof window === 'undefined') return;
    const t0 = Date.now();
    const res = await fetch(`${window.location.origin}/?__clock=${t0}`, { method: 'HEAD', cache: 'no-store' });
    const t1 = Date.now();
    const dateHeader = res.headers.get('date');
    const serverMs = dateHeader ? Date.parse(dateHeader) : NaN;
    const age = Number(res.headers.get('age')) || 0;
    const rtt = t1 - t0;
    if (!Number.isFinite(serverMs) || rtt > SYNC_MAX_RTT_MS) { report('failed', rtt, null); return; }
    // Date is truncated to the second (+500ms is its expected value); Age is how long a cached
    // response had been held. Compare with local time at the midpoint of the round trip.
    const raw = Math.round(serverMs + 500 + age * 1000 - (t0 + rtt / 2));
    if (Math.abs(raw) > SYNC_MAX_ABS_OFFSET_MS) { report('failed', rtt, raw); return; }
    if (Math.abs(raw) < SYNC_MIN_CORRECTION_MS) { offsetMs = 0; report('in-sync', rtt, raw); return; }
    offsetMs = raw;
    report('synced', rtt, raw);
  } catch {
    offsetMs = 0;
    report('failed', null, null);
  }
};
